import {
  Badge,
  Card,
  Checkbox,
  Flex,
  Form,
  InputNumber,
  Radio,
  Select,
  Space,
  theme,
  Typography,
} from 'antd';
import { type ReactNode, useMemo } from 'react';
import { type WizardValues } from './site-menu-order-wizard.mapper';
import { type ScheduledMealOptionsDto } from '../../../../../../dto/order-options/site-order-options-dto';
import { type Site } from '../../../../../../entities';
import utils from '../../../../../../utils/utils';

const FieldLabel = ({
  label,
  required = false,
}: {
  label: string;
  required?: boolean;
}): ReactNode => (
  <Flex style={{ width: '30%' }}>
    <Typography.Text strong>
      {required && <span style={{ color: 'red' }}>*</span>} {label}
    </Typography.Text>
  </Flex>
);

const FieldRow = ({
  label,
  required,
  width = '70%',
  align = 'center',
  children,
}: {
  label: string;
  required?: boolean;
  width?: string;
  align?: 'center' | 'flex-start';
  children: ReactNode;
}): ReactNode => (
  <Flex style={{ width: '100%' }}>
    <FieldLabel label={label} required={required} />
    <Flex align={align} justify="flex-start" style={{ width }}>
      {children}
    </Flex>
  </Flex>
);

/** The options of a comma separated list, sorted */
const toSortedOptions = (values: string): Array<{ label: string; value: string }> =>
  values
    .split(',')
    .sort()
    .map((value) => ({ label: value, value }));

/** A checkbox showing a multiple select, which is cleared when the checkbox is unchecked */
const OptionalMultipleSelect = ({
  toggleName,
  name,
  checkboxLabel,
  selectLabel,
  placeholder,
  values,
}: {
  toggleName: 'showDiets' | 'showPreferences';
  name: 'specialDiets' | 'preferences';
  checkboxLabel: string;
  selectLabel: string;
  placeholder: string;
  values: string;
}): ReactNode => {
  const form = Form.useFormInstance<WizardValues>();
  const isShown = Form.useWatch(toggleName, form);
  const options = useMemo(() => toSortedOptions(values), [values]);
  return (
    <>
      <Flex style={{ marginBottom: 20 }}>
        <Form.Item noStyle name={toggleName} valuePropName="checked">
          <Checkbox
            onChange={() => {
              form.setFieldValue(name, null);
            }}
          >
            {checkboxLabel}
          </Checkbox>
        </Form.Item>
      </Flex>
      {isShown && (
        <FieldRow label={selectLabel}>
          <Form.Item name={name}>
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 300 }}
              placeholder={placeholder}
              options={options}
            />
          </Form.Item>
        </FieldRow>
      )}
    </>
  );
};

const MealContents = ({ meal }: { meal: ScheduledMealOptionsDto }): ReactNode => {
  const { token } = theme.useToken();
  const quantity = Form.useWatch('quantity');
  return (
    <Flex style={{ width: '30%', paddingBlockEnd: 20 }}>
      <Card
        styles={{
          header: { textAlign: 'left', paddingInline: 20 },
          body: { paddingInline: 20, textAlign: 'left' },
        }}
        variant="borderless"
        style={{ minWidth: 300, minHeight: '60%' }}
        size="small"
        title={
          <Typography.Text style={{ justifySelf: 'flex-start' }}>
            {meal.name} × {quantity}
          </Typography.Text>
        }
      >
        <Flex vertical gap="small">
          {(meal.mealItemsNames?.length ?? 0) > 0 && (
            <Typography.Text>Each contains:</Typography.Text>
          )}
          {meal.mealItemsNames.map((mealItemName, idx) => (
            <Badge key={idx} color={token.colorPrimary} text={mealItemName} />
          ))}
        </Flex>
      </Card>
    </Flex>
  );
};

interface MealStepProps {
  site: Site;
  availableMeals: ScheduledMealOptionsDto[];
  selectedMeal: ScheduledMealOptionsDto | undefined;
  onMealChange: (mealId: string) => void;
}

/** First step: the meal, its route and quantity, and who it is for */
const MealStep = ({
  site,
  availableMeals,
  selectedMeal,
  onMealChange,
}: MealStepProps): ReactNode => {
  const { token } = theme.useToken();
  const mealCategory = selectedMeal?.mealCategory;
  const routeOptions = useMemo(
    () => [
      { label: 'N/A', value: null },
      ...site.routes.map((route) => ({ label: route.name, value: route.id })),
    ],
    [site.routes]
  );
  const mealOptions = useMemo(
    () => availableMeals.map((meal) => ({ value: String(meal.id), label: meal.mealCategory.name })),
    [availableMeals]
  );

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Space.Compact
        direction="vertical"
        style={{ paddingInline: 20, display: 'flex', minHeight: 300, width: '60%' }}
      >
        <FieldRow label="Route" required>
          <Form.Item name="routeId">
            <Select options={routeOptions} style={{ width: 120 }} />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Meal Category" required width="60%" align="flex-start">
          <Form.Item
            name={['scheduledMeal', 'id']}
            rules={[{ required: true, message: 'Missing meal category' }]}
          >
            <Select
              showSearch
              style={{ width: 300 }}
              placeholder="Select"
              optionFilterProp="label"
              onChange={onMealChange}
              options={mealOptions}
            />
          </Form.Item>
          <Typography.Text
            type="secondary"
            style={{ marginBlockStart: 5, marginLeft: token.marginSM }}
          >
            {mealCategory?.description}
          </Typography.Text>
        </FieldRow>
        {mealCategory?.allowC1OrC2 && (
          <FieldRow label="Service Type" required width="40%">
            <Form.Item name="serviceType">
              <Radio.Group>
                <Flex>
                  <Radio value={utils.serviceTypesLabels.C1}>C1</Radio>
                  <Radio value={utils.serviceTypesLabels.C2}>C2</Radio>
                </Flex>
              </Radio.Group>
            </Form.Item>
          </FieldRow>
        )}
        <FieldRow label="Quantity" required>
          <Form.Item
            name="quantity"
            rules={[
              { required: true },
              {
                validator: async (_, value) => {
                  const minAllowed = mealCategory?.minQuantityAllowed ?? 1;
                  if (value && value >= minAllowed) return;
                  throw new Error(`You must select more than ${minAllowed} ${mealCategory?.name}`);
                },
              },
            ]}
          >
            <InputNumber min={0} precision={0} />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Main Type" required>
          <Form.Item name="mainType">
            <Radio.Group disabled={mealCategory && !mealCategory.hasDiabeticType}>
              <Flex vertical>
                <Radio value="Regular">Regular</Radio>
                <Radio value="Diabetic">Diabetic</Radio>
              </Flex>
            </Radio.Group>
          </Form.Item>
        </FieldRow>
        {!!mealCategory?.diets && (
          <OptionalMultipleSelect
            toggleName="showDiets"
            name="specialDiets"
            checkboxLabel="Add special diets?"
            selectLabel="Select multiple diets"
            placeholder="Please select diets"
            values={mealCategory.diets}
          />
        )}
        {!!mealCategory?.preferences && (
          <OptionalMultipleSelect
            toggleName="showPreferences"
            name="preferences"
            checkboxLabel="Add preferences?"
            selectLabel="Select multiple preferences"
            placeholder="Please select preferences"
            values={mealCategory.preferences}
          />
        )}
      </Space.Compact>
      {selectedMeal?.name && <MealContents meal={selectedMeal} />}
    </Space.Compact>
  );
};

export default MealStep;
